import React, { useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ThankYouPage.scss";
import Conng from "../../assets/gif/cong.gif";
import { downloadProposalForm } from "../../api/paymentService";
import { logError } from "../../common/utils/logger";
import BrandLoader from "../../common/components/BrandLoader";
import { fetchPolicyStatus } from "../../api/policyActions";
import { journeyActions } from "../../store/journeySlice";

export const ThankYouPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const proposerName = useSelector(
    (state) => state.journey?.leadFormData?.fullName,
  );

  const proposalNumber = useSelector(
    (state) => state.journey?.applicationNumber,
  );

  const leadData = useSelector((state) => state.journey.leadFormData || {});

  const applicationNumber =
    useSelector((state) => state.journey?.applicationNumber) ||
    leadData?.applicationNumber;

  const [proposalDownloading, setProposalDownloading] = useState(false);
  const [proposalError, setProposalError] = useState(null);

  const handleDownloadProposalForm = useCallback(async () => {
    if (!proposalNumber || String(proposalNumber).trim() === "") {
      setProposalError(
        "Application number is missing. Cannot download proposal form.",
      );
      return;
    }
    setProposalError(null);
    setProposalDownloading(true);
    try {
      await downloadProposalForm({
        applicationNumber: String(proposalNumber).trim(),
      });
    } catch (e) {
      logError("[ThankYou] Proposal form download failed", e);
      setProposalError(
        e?.message || "Failed to download proposal form. Please try again.",
      );
    } finally {
      setProposalDownloading(false);
    }
  }, [proposalNumber]);

  //   window.location.href = "/track-your-application";
  // };

  const handleTrackApplication = async () => {
    try {
      const appNoForPolicyStatus = String(applicationNumber || "",).trim();

      if (!appNoForPolicyStatus) {
        setProposalError("Application number is missing. Please try again.");
        return;
      }

      const policyStatusResp =
        await fetchPolicyStatus(appNoForPolicyStatus)();

      const policyStatus = policyStatusResp?.response?.Policy_Status;

      dispatch(journeyActions.setPolicyStatusData(policyStatus));

      navigate("/track-your-application");
    } catch (e) {
      logError("Failed to fetch policy status", e);
      setProposalError("Unable to track application. Please try again.");
    }
  };

  return (
    <div className="thankyou-container">
      {proposalDownloading && (
        <div className="quote-premium-loader-overlay" aria-live="polite">
          <BrandLoader color="#97262A" loading={true} size={15} />
        </div>
      )}
      <div className="thankyou-container-wraper">
        {/* Success Banner */}
        <div className="success-banner">
          <div className="success-icon">
            <img src={Conng} alt="Conng" />
          </div>

          <div className="success-banner-footer">
            <h4 className="bold">Thank you!</h4>
            <h4 className="bold">
              Your proposal has been successfully submitted
            </h4>

            <p className="small-body regular">
              We've received your payment, application details, and documents.
              Your policy is now under review.
            </p>
          </div>
        </div>

        {/* Proposal Details */}
        <div className="proposal-bar">
          <span>
            Proposer Name: <b>{proposerName}</b> | Proposal Number:{" "}
            <b>{proposalNumber}</b>
          </span>
        </div>

        {/* Progress Steps */}
        <div className="progress-section">
          <div className="progress-step completed">
            <div className="tick">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#03BF43" />
                <path
                  d="M16.3592 8.3934C16.1837 8.21783 15.8993 8.21783 15.7242 8.3934L9.75396 14.3637L8.27449 12.8852C8.09943 12.7096 7.81453 12.7096 7.63948 12.8852C7.46392 13.0607 7.46392 13.3452 7.63948 13.5202L9.38025 15.261C9.42646 15.3072 9.48086 15.3395 9.53835 15.3616C9.60972 15.4052 9.68723 15.4345 9.76782 15.4345C9.8828 15.4345 9.9978 15.3908 10.0856 15.3031L16.3592 9.02892C16.5348 8.85337 16.5348 8.56897 16.3592 8.39341V8.3934H16.3592Z"
                  fill="white"
                />
              </svg>
            </div>
            <h6 className="large-body semibold m-0">Payment Completed</h6>
            <p className="text-caption regular">
              Your payment has been successfully processed.
            </p>
          </div>

          <div className="progress-step completed">
            <div className="tick">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#03BF43" />
                <path
                  d="M16.3592 8.3934C16.1837 8.21783 15.8993 8.21783 15.7242 8.3934L9.75396 14.3637L8.27449 12.8852C8.09943 12.7096 7.81453 12.7096 7.63948 12.8852C7.46392 13.0607 7.46392 13.3452 7.63948 13.5202L9.38025 15.261C9.42646 15.3072 9.48086 15.3395 9.53835 15.3616C9.60972 15.4052 9.68723 15.4345 9.76782 15.4345C9.8828 15.4345 9.9978 15.3908 10.0856 15.3031L16.3592 9.02892C16.5348 8.85337 16.5348 8.56897 16.3592 8.39341V8.3934H16.3592Z"
                  fill="white"
                />
              </svg>
            </div>
            <h6 className="large-body semibold m-0">Application Submitted</h6>
            <p className="text-caption regular">
              Your details have been submitted for verification.
            </p>
          </div>

          <div className="progress-step completed">
            <div className="tick">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#03BF43" />
                <path
                  d="M16.3592 8.3934C16.1837 8.21783 15.8993 8.21783 15.7242 8.3934L9.75396 14.3637L8.27449 12.8852C8.09943 12.7096 7.81453 12.7096 7.63948 12.8852C7.46392 13.0607 7.46392 13.3452 7.63948 13.5202L9.38025 15.261C9.42646 15.3072 9.48086 15.3395 9.53835 15.3616C9.60972 15.4052 9.68723 15.4345 9.76782 15.4345C9.8828 15.4345 9.9978 15.3908 10.0856 15.3031L16.3592 9.02892C16.5348 8.85337 16.5348 8.56897 16.3592 8.39341V8.3934H16.3592Z"
                  fill="white"
                />
              </svg>
            </div>
            <h6 className="large-body semibold m-0">Documents Uploaded</h6>
            <p className="text-caption regular">
              All required documents have been received.
            </p>
          </div>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="next-section">
        <div className="next-section-header">
          <span className="small-body semibold">What happens next?</span>
        </div>

        <div className="next-items">
          <div className="next-items-tab have-bg">
            <svg
              width="19"
              height="19"
              viewBox="0 0 19 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.2753 1.30575C11.3008 1.21057 11.3447 1.12133 11.4047 1.04315C11.4647 0.96496 11.5394 0.899354 11.6248 0.850077C11.7101 0.8008 11.8043 0.768817 11.902 0.755956C11.9996 0.743095 12.0989 0.749608 12.1941 0.775123C13.5844 1.13789 14.853 1.86473 15.869 2.88078C16.8851 3.89684 17.6119 5.16538 17.9747 6.55575C18.0002 6.65092 18.0067 6.75018 17.9939 6.84786C17.981 6.94555 17.949 7.03974 17.8997 7.12506C17.8505 7.21039 17.7849 7.28516 17.7067 7.34512C17.6285 7.40508 17.5393 7.44904 17.4441 7.4745C17.3807 7.49114 17.3155 7.49964 17.25 7.49981C17.0847 7.49981 16.9241 7.44521 16.793 7.34451C16.662 7.2438 16.5679 7.10263 16.5253 6.94293C16.2295 5.80792 15.6363 4.77235 14.8069 3.94296C13.9775 3.11356 12.9419 2.52036 11.8069 2.2245C11.7116 2.19914 11.6223 2.15525 11.544 2.09533C11.4657 2.03542 11.4 1.96066 11.3506 1.87533C11.3013 1.79 11.2692 1.69577 11.2563 1.59803C11.2433 1.5003 11.2498 1.40098 11.2753 1.30575ZM11.0569 5.2245C12.3497 5.5695 13.1803 6.40012 13.5253 7.69293C13.5679 7.85263 13.662 7.9938 13.793 8.09451C13.9241 8.19521 14.0847 8.24981 14.25 8.24981C14.3155 8.24964 14.3807 8.24114 14.4441 8.2245C14.5393 8.19904 14.6285 8.15508 14.7067 8.09512C14.7849 8.03516 14.8505 7.96039 14.8997 7.87506C14.949 7.78974 14.981 7.69555 14.9939 7.59786C15.0067 7.50018 15.0002 7.40092 14.9747 7.30575C14.4947 5.5095 13.2403 4.25512 11.4441 3.77512C11.2519 3.72378 11.0471 3.75089 10.8749 3.85049C10.7027 3.95009 10.5771 4.11402 10.5258 4.30622C10.4744 4.49842 10.5016 4.70314 10.6012 4.87535C10.7008 5.04756 10.8647 5.17315 11.0569 5.2245ZM18.7388 14.1636C18.5716 15.4339 17.9477 16.6 16.9837 17.4439C16.0196 18.2879 14.7813 18.7521 13.5 18.7498C6.05626 18.7498 8.4831e-06 12.6936 8.4831e-06 5.24981C-0.00228837 3.96852 0.461903 2.73021 1.30588 1.76615C2.14986 0.802093 3.31592 0.178223 4.58626 0.0110603C4.9075 -0.0281639 5.2328 0.037556 5.51362 0.198409C5.79444 0.359262 6.0157 0.606621 6.14438 0.90356L8.12438 5.32387V5.33512C8.2229 5.56242 8.26359 5.81059 8.24282 6.05744C8.22204 6.3043 8.14044 6.54217 8.00532 6.74981C7.98845 6.77512 7.97063 6.79856 7.95188 6.822L6.00001 9.13575C6.7022 10.5626 8.1947 12.042 9.64032 12.7461L11.9222 10.8045C11.9446 10.7857 11.9681 10.7681 11.9925 10.752C12.2 10.6136 12.4387 10.5292 12.687 10.5062C12.9353 10.4833 13.1854 10.5227 13.4147 10.6207L13.4269 10.6264L17.8434 12.6054C18.1409 12.7337 18.3889 12.9547 18.5503 13.2356C18.7116 13.5164 18.7778 13.842 18.7388 14.1636ZM17.25 13.9761C17.25 13.9761 17.2434 13.9761 17.2397 13.9761L12.8334 12.0026L10.5506 13.9442C10.5285 13.9629 10.5053 13.9805 10.4813 13.9967C10.2654 14.1407 10.0159 14.2262 9.75708 14.245C9.49828 14.2637 9.23904 14.215 9.0047 14.1036C7.24876 13.2551 5.49845 11.5179 4.64907 9.78075C4.5366 9.54811 4.48613 9.29037 4.50255 9.03249C4.51898 8.77462 4.60174 8.52536 4.74282 8.30887C4.75872 8.28345 4.77659 8.25932 4.79626 8.23669L6.75001 5.92012L4.78126 1.51387C4.78089 1.51013 4.78089 1.50636 4.78126 1.50262C3.87212 1.62122 3.03739 2.06715 2.43339 2.75692C1.8294 3.44669 1.49755 4.33297 1.50001 5.24981C1.50348 8.43134 2.76888 11.4816 5.01856 13.7313C7.26825 15.9809 10.3185 17.2463 13.5 17.2498C14.4163 17.253 15.3023 16.9223 15.9925 16.3196C16.6827 15.7169 17.1297 14.8835 17.25 13.9751V13.9761Z"
                fill="#AE272C"
              />
            </svg>
            <div className="next-items-tab-wraper">
              <h6 className="m-0 regular-body semibold">
                Operational Checks
              </h6>
              <p className="small-body regular">
                Our operations team will check your application and supporting documents uploaded.
                In case or any error, mismatch or incompleteness they will reach out to you to ensure
                accuracy and compliance.
              </p>
            </div>
          </div>
          <div className="next-items-tab">
            <div className="next-items-tab-wraper">
              <label className="m-0 small-body medium">
                After operational checks, your application proceeds for underwriting
                 where you will be asked to undergo medical examinations.
              </label>
              <ul>
                <li>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.3013 18.2401L14.6073 13.547C15.9678 11.9136 16.6462 9.81853 16.5014 7.69766C16.3566 5.5768 15.3998 3.5934 13.8299 2.16007C12.26 0.726741 10.1979 -0.0461652 8.07263 0.0021347C5.94738 0.0504346 3.92256 0.916222 2.41939 2.41939C0.916222 3.92256 0.0504346 5.94738 0.0021347 8.07263C-0.0461652 10.1979 0.726741 12.26 2.16007 13.8299C3.5934 15.3998 5.5768 16.3566 7.69766 16.5014C9.81853 16.6462 11.9136 15.9678 13.547 14.6073L18.2401 19.3013C18.3098 19.371 18.3925 19.4263 18.4836 19.464C18.5746 19.5017 18.6722 19.5211 18.7707 19.5211C18.8693 19.5211 18.9669 19.5017 19.0579 19.464C19.1489 19.4263 19.2317 19.371 19.3013 19.3013C19.371 19.2317 19.4263 19.1489 19.464 19.0579C19.5017 18.9669 19.5211 18.8693 19.5211 18.7707C19.5211 18.6722 19.5017 18.5746 19.464 18.4836C19.4263 18.3925 19.371 18.3098 19.3013 18.2401ZM1.52072 8.27072C1.52072 6.9357 1.9166 5.63065 2.6583 4.52062C3.4 3.41059 4.45421 2.54543 5.68761 2.03454C6.92101 1.52364 8.27821 1.38997 9.58758 1.65042C10.897 1.91087 12.0997 2.55375 13.0437 3.49775C13.9877 4.44176 14.6306 5.64449 14.891 6.95386C15.1515 8.26323 15.0178 9.62043 14.5069 10.8538C13.996 12.0872 13.1309 13.1414 12.0208 13.8831C10.9108 14.6248 9.60575 15.0207 8.27072 15.0207C6.48112 15.0187 4.76538 14.3069 3.49994 13.0415C2.2345 11.7761 1.52271 10.0603 1.52072 8.27072Z"
                      fill="#AE272C"
                    />
                  </svg>

                  <span className="small-body regular">
                    Underwriting team will review your application and documents
                  </span>
                </li>
                <li>
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 21 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.6744 8.13937C19.3209 7.77 18.9553 7.38938 18.8175 7.05469C18.69 6.74813 18.6825 6.24 18.675 5.74781C18.6609 4.83281 18.6459 3.79594 17.925 3.075C17.2041 2.35406 16.1672 2.33906 15.2522 2.325C14.76 2.3175 14.2519 2.31 13.9453 2.1825C13.6116 2.04469 13.23 1.67906 12.8606 1.32562C12.2137 0.704062 11.4788 0 10.5 0C9.52125 0 8.78719 0.704062 8.13937 1.32562C7.77 1.67906 7.38938 2.04469 7.05469 2.1825C6.75 2.31 6.24 2.3175 5.74781 2.325C4.83281 2.33906 3.79594 2.35406 3.075 3.075C2.35406 3.79594 2.34375 4.83281 2.325 5.74781C2.3175 6.24 2.31 6.74813 2.1825 7.05469C2.04469 7.38844 1.67906 7.77 1.32562 8.13937C0.704062 8.78625 0 9.52125 0 10.5C0 11.4788 0.704062 12.2128 1.32562 12.8606C1.67906 13.23 2.04469 13.6106 2.1825 13.9453C2.31 14.2519 2.3175 14.76 2.325 15.2522C2.33906 16.1672 2.35406 17.2041 3.075 17.925C3.79594 18.6459 4.83281 18.6609 5.74781 18.675C6.24 18.6825 6.74813 18.69 7.05469 18.8175C7.38844 18.9553 7.77 19.3209 8.13937 19.6744C8.78625 20.2959 9.52125 21 10.5 21C11.4788 21 12.2128 20.2959 12.8606 19.6744C13.23 19.3209 13.6106 18.9553 13.9453 18.8175C14.2519 18.69 14.76 18.6825 15.2522 18.675C16.1672 18.6609 17.2041 18.6459 17.925 17.925C18.6459 17.2041 18.6609 16.1672 18.675 15.2522C18.6825 14.76 18.69 14.2519 18.8175 13.9453C18.9553 13.6116 19.3209 13.23 19.6744 12.8606C20.2959 12.2137 21 11.4788 21 10.5C21 9.52125 20.2959 8.78719 19.6744 8.13937ZM18.5916 11.8228C18.1425 12.2916 17.6775 12.7763 17.4309 13.3716C17.1947 13.9434 17.1844 14.5969 17.175 15.2297C17.1656 15.8859 17.1553 16.5731 16.8638 16.8638C16.5722 17.1544 15.8897 17.1656 15.2297 17.175C14.5969 17.1844 13.9434 17.1947 13.3716 17.4309C12.7763 17.6775 12.2916 18.1425 11.8228 18.5916C11.3541 19.0406 10.875 19.5 10.5 19.5C10.125 19.5 9.64219 19.0387 9.17719 18.5916C8.71219 18.1444 8.22375 17.6775 7.62844 17.4309C7.05656 17.1947 6.40313 17.1844 5.77031 17.175C5.11406 17.1656 4.42688 17.1553 4.13625 16.8638C3.84562 16.5722 3.83437 15.8897 3.825 15.2297C3.81562 14.5969 3.80531 13.9434 3.56906 13.3716C3.3225 12.7763 2.8575 12.2916 2.40844 11.8228C1.95937 11.3541 1.5 10.875 1.5 10.5C1.5 10.125 1.96125 9.64219 2.40844 9.17719C2.85562 8.71219 3.3225 8.22375 3.56906 7.62844C3.80531 7.05656 3.81562 6.40313 3.825 5.77031C3.83437 5.11406 3.84469 4.42688 4.13625 4.13625C4.42781 3.84562 5.11031 3.83437 5.77031 3.825C6.40313 3.81562 7.05656 3.80531 7.62844 3.56906C8.22375 3.3225 8.70844 2.8575 9.17719 2.40844C9.64594 1.95937 10.125 1.5 10.5 1.5C10.875 1.5 11.3578 1.96125 11.8228 2.40844C12.2878 2.85562 12.7763 3.3225 13.3716 3.56906C13.9434 3.80531 14.5969 3.81562 15.2297 3.825C15.8859 3.83437 16.5731 3.84469 16.8638 4.13625C17.1544 4.42781 17.1656 5.11031 17.175 5.77031C17.1844 6.40313 17.1947 7.05656 17.4309 7.62844C17.6775 8.22375 18.1425 8.70844 18.5916 9.17719C19.0406 9.64594 19.5 10.125 19.5 10.5C19.5 10.875 19.0387 11.3578 18.5916 11.8228ZM14.7806 7.71937C14.8504 7.78903 14.9057 7.87175 14.9434 7.96279C14.9812 8.05384 15.0006 8.15144 15.0006 8.25C15.0006 8.34856 14.9812 8.44616 14.9434 8.5372C14.9057 8.62825 14.8504 8.71097 14.7806 8.78063L9.53063 14.0306C9.46097 14.1004 9.37825 14.1557 9.2872 14.1934C9.19616 14.2312 9.09856 14.2506 9 14.2506C8.90144 14.2506 8.80384 14.2312 8.7128 14.1934C8.62175 14.1557 8.53903 14.1004 8.46937 14.0306L6.21937 11.7806C6.07864 11.6399 5.99958 11.449 5.99958 11.25C5.99958 11.051 6.07864 10.8601 6.21937 10.7194C6.36011 10.5786 6.55098 10.4996 6.75 10.4996C6.94902 10.4996 7.13989 10.5786 7.28063 10.7194L9 12.4397L13.7194 7.71937C13.789 7.64964 13.8717 7.59432 13.9628 7.55658C14.0538 7.51884 14.1514 7.49941 14.25 7.49941C14.3486 7.49941 14.4462 7.51884 14.5372 7.55658C14.6283 7.59432 14.711 7.64964 14.7806 7.71937Z"
                      fill="#AE272C"
                    />
                  </svg>

                  <span className="small-body regular">
                    Once approved, your policy will be issued and shared with you within a couple of
                    business days.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Buttons */}
      <div className="proposal-form-actions">
        <button
          className="proposal-btn-continue  btn primary-button regular"
          onClick={handleTrackApplication}
        >
          Track Application
        </button>

        <button
          type="button"
          className={`proposal-btn-continue btn secondary-button regular ${proposalDownloading ? "wait" : "pointer"}`}
          onClick={handleDownloadProposalForm}
          disabled={proposalDownloading}
        >
          {proposalDownloading ? "Downloading..." : "Download Proposal Form"}
        </button>
        {proposalError ? (
          <span className="proposal-form-error-text">{proposalError}</span>
        ) : null}
      </div>
    </div>
  );
};

export default ThankYouPage;
